import { selectActiveCadModel } from "@/store/cad/cad-slice";
import { SplitDirection } from "@/store/modes/alignment-ui-types";
import {
  selectCrossSectionEnabledForTomographicView,
  selectSheetCadAlignmentReference,
  selectSheetCadAlignmentSplitDirection,
  selectSheetElevation,
} from "@/store/modes/sheet-cad-alignment-mode-selectors";
import {
  setCrossSectionEnabled,
  setSheetCadAlignmentSplitDirection,
  SheetCadAlignReference,
} from "@/store/modes/sheet-cad-alignment-mode-slice";
import { useAppDispatch, useAppSelector } from "@/store/store-hooks";
import { isIElementAreaSection } from "@faro-lotv/ielement-types";
import { selectAncestor } from "@faro-lotv/project-source";
import { useCallback, useMemo, type JSX } from "react";
import {
  AlignmentModeSplitScreenOverlay,
  CrossSectionProps,
} from "../alignment-modes-commons/alignment-mode-split-screen-overlay";
import { useSheetSelectedForAlignment } from "../mode-data-context";

/** @returns The sheet and cad alignment split screen */
export function SheetCadAlignmentSplitScreen(): JSX.Element {
  const dispatch = useAppDispatch();
  const splitDirection = useAppSelector(selectSheetCadAlignmentSplitDirection);

  // callback function for changing split-screen direction
  const changeSplitDirection = useCallback(() => {
    const newDirection =
      splitDirection === SplitDirection.horizontalSplit
        ? SplitDirection.verticalSplit
        : SplitDirection.horizontalSplit;
    dispatch(setSheetCadAlignmentSplitDirection(newDirection));
  }, [dispatch, splitDirection]);

  const isCrossSectionEnabled = useAppSelector(
    selectCrossSectionEnabledForTomographicView,
  );
  const sheetElevation = useAppSelector(selectSheetElevation);
  const sheet = useSheetSelectedForAlignment("sheetAndCad");
  const area = useAppSelector(selectAncestor(sheet, isIElementAreaSection));
  const activeCad = useAppSelector(selectActiveCadModel);
  const alignmentReference = useAppSelector(selectSheetCadAlignmentReference);

  const cadCrossSectionProps: CrossSectionProps = useMemo(
    () => ({
      enabled: isCrossSectionEnabled,
      elevation: sheetElevation,
      onToggleCrossSection: () => {
        dispatch(setCrossSectionEnabled(!isCrossSectionEnabled));
      },
    }),
    [dispatch, isCrossSectionEnabled, sheetElevation],
  );

  return (
    <AlignmentModeSplitScreenOverlay
      splitDirection={splitDirection}
      onChangeSplitDirection={changeSplitDirection}
      firstScreenCrossSection={
        alignmentReference === SheetCadAlignReference.sheet
          ? cadCrossSectionProps
          : undefined
      }
      secondScreenCrossSection={
        alignmentReference === SheetCadAlignReference.bimModel
          ? cadCrossSectionProps
          : undefined
      }
      alignedElement={area}
      referenceElement={activeCad}
    />
  );
}
